// messages.js - Handles message input and API communication

import store from "./store.js";
import { apiUrl, agentId, contextMessageCount } from "./api-settings.js";
import {
  showNotification,
  formatMessageContent,
  showConfirm,
} from "./utils.js";
import {
  saveConversations,
  renderConversationsList,
  renderMessages,
  addThinkingIndicator,
  removeThinkingIndicator,
  createNewConversation,
  reportInaccurateContent,
  createWalletScanResultHTML,
  createMultipleWalletScanResultHTML
} from "./conversations.js";

import { featureSystem } from '../features.js';


// Variables to track guest message limits and manage message input
let guestMessageCount = 0;
let guestMaxMessages = 5; // Limite par défaut pour les utilisateurs non connectés

/**
 * Set up the message handling functionality
 */
function setupMessageHandling() {
  const messageInput = document.getElementById("messageInput");
  const sendBtn = document.getElementById("sendBtn");

  if (messageInput) {
    messageInput.addEventListener("input", handleInputChange);
    messageInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        
        // Vérifier si une réponse est déjà en attente
        let currentConversationId = store.getCurrentConversationId();
        const pendingResponses = store.getPendingResponses();
        if (pendingResponses && pendingResponses[currentConversationId]) {
          return;
        }
        
        // Masquer les suggestions de feature avant d'envoyer le message
        if (window.featureSystem && typeof window.featureSystem.hideSuggestions === 'function') {
          window.featureSystem.hideSuggestions();
        } else if (typeof featureSystem !== 'undefined' && typeof featureSystem.hideSuggestions === 'function') {
          featureSystem.hideSuggestions();
        }
        
        sendMessage();
      }
    });
  }

  if (sendBtn) {
    sendBtn.addEventListener("click", (e) => {
      // Vérifier si une réponse est déjà en attente
      let currentConversationId = store.getCurrentConversationId();
      const pendingResponses = store.getPendingResponses();
      if (pendingResponses && pendingResponses[currentConversationId]) {
        // Ne pas permettre l'envoi si une réponse est déjà en attente
        e.preventDefault();
        return;
      }
      
      sendMessage();
    });
  }

  // Set up message menu interactions for reporting inaccurate content
  setupMessageMenus();

  // Load guest usage if in guest mode
  if (store.getIsGuestMode()) {
    loadGuestUsage();
  }

  // Add event listener for the 'Learn More' link in messages
  const messagesContainer = document.getElementById("messagesContainer");
  if (messagesContainer) {
    messagesContainer.addEventListener("click", (e) => {
      const learnMoreLink = e.target.closest("a.learn-more-link");
      if (learnMoreLink) {
        e.preventDefault(); // Prevent default link behavior
        const learnMoreModal = document.getElementById("learnMoreModal"); // Assumed ID
        if (learnMoreModal) {
          learnMoreModal.style.display = "flex"; // Or however you show your modal
        }
      }
    });
  }
}

/**
 * Set up the message menu functionality
 */
function setupMessageMenus() {
  document.addEventListener("click", (e) => {
    const menuBtn = e.target.closest(".message-menu-btn");
    if (menuBtn) {
      const menu = menuBtn.nextElementSibling;
      menu.classList.toggle("active");
      e.stopPropagation();
    }

    const reportItem = e.target.closest(".report-item");
    if (reportItem) {
      const message = reportItem.closest(".message");
      reportInaccurateContent(message);

      reportItem.closest(".message-menu").classList.remove("active");
      e.stopPropagation();
    }

    if (
      !e.target.closest(".message-menu") &&
      !e.target.closest(".message-menu-btn")
    ) {
      document.querySelectorAll(".message-menu.active").forEach((menu) => {
        menu.classList.remove("active");
      });
    }
  });
}

/**
 * Handle input changes in the message box
 */
function handleInputChange() {
  const messageInput = document.getElementById("messageInput");
  const sendBtn = document.getElementById("sendBtn");

  messageInput.style.height = "auto";
  messageInput.style.height = messageInput.scrollHeight + "px";

  if (sendBtn) {
    sendBtn.disabled = messageInput.value.trim() === "";
  }
}

/**
 * Send a message to the API
 */
async function sendMessage() {
    try {
      const messageInput = document.getElementById("messageInput");
      const sendBtn = document.getElementById("sendBtn");
      let currentConversationId = store.getCurrentConversationId();
  
      let content = messageInput.value.trim();
      if (content === "") return;

      // Nettoyage des placeholders s'il s'agit d'une commande de feature
      if (messageInput.dataset.featureCommand === 'true') {
        // Vérifier si la commande contient encore les placeholders
        const commandPrefix = messageInput.dataset.commandPrefix || "";
        const addressPlaceholder = messageInput.dataset.addressPlaceholder || "_________________";
        const networkPlaceholder = messageInput.dataset.networkPlaceholder || "______";
        
        // Si la commande contient encore les placeholders de base ou le placeholder "__", ne pas envoyer
        if (content.includes(addressPlaceholder) || 
            content.includes(networkPlaceholder) || 
            content.includes('__')) {
          // Ne pas envoyer de message incomplet
          showNotification("Please complete all information before sending", "warning");
          
          // Si c'est une commande "scan specific wallet" avec le placeholder "__"
          if (content.includes('__') && 
             (content.startsWith('scan specific wallet') || content.startsWith('scan wallet number'))) {
            // Importer dynamiquement featureSystem pour utiliser showWalletNumberDropdown
            try {
              const { featureSystem } = await import('../features.js');
              const wallets = featureSystem.getStoredWallets();
              if (wallets && wallets.length > 0) {
                featureSystem.showWalletNumberDropdown(wallets);
              }
            } catch (error) {
              console.error("Failed to import feature system or show wallet dropdown:", error);
            }
          }
          
          if (sendBtn) sendBtn.disabled = false;
          if (messageInput) messageInput.disabled = false;
          return;
        }
        
        // Réinitialiser le mode feature
        delete messageInput.dataset.featureCommand;
        delete messageInput.dataset.addressPlaceholder;
        delete messageInput.dataset.networkPlaceholder;
        delete messageInput.dataset.commandPrefix;
      }

      // Check if this is a feature command
      const featureMatch = featureSystem.processMessage(content);
      
      if (featureMatch) {
        // Handle as a feature command
        const { feature, params } = featureMatch;
        
        // Disable send button to prevent double submissions
        if (sendBtn) sendBtn.disabled = true;
        if (messageInput) messageInput.disabled = true;
        
        // IMPORTANT: Make sure we have a valid conversation ID
        if (!currentConversationId) {
          currentConversationId = createNewConversation();
          store.setCurrentConversationId(currentConversationId);
        }
        
        // Get updated conversations list after possibly creating a new one
        let conversations = store.getConversations();
        let conversationIndex = conversations.findIndex(
          (c) => c.id === currentConversationId
        );
        
        if (conversationIndex === -1) {
          // Create a new conversation if the previous attempt failed
          currentConversationId = createNewConversation();
          store.setCurrentConversationId(currentConversationId);
          
          // Get updated conversations list again
          conversations = store.getConversations();
          conversationIndex = conversations.findIndex(
            (c) => c.id === currentConversationId
          );
          
          if (conversationIndex === -1) {
            showNotification("Erreur lors de la création de la conversation", "error");
            if (sendBtn) sendBtn.disabled = false;
            if (messageInput) messageInput.disabled = false;
            return;
          }
        }
        
        // Add user message to conversation
        const userMessage = {
          role: "user",
          content: content,
          timestamp: new Date().toISOString(),
        };
        
        conversations[conversationIndex].messages.push(userMessage);
        store.setConversations(conversations);
        saveConversations();
        
        // Re-render messages
        renderMessages(conversations[conversationIndex].messages);
        
        // Clear input and resize
        messageInput.value = "";
        messageInput.style.height = "auto";
        
        // Add thinking indicator while processing
        addThinkingIndicator(currentConversationId);
        
        try {
          // Execute the feature handler
          const result = await feature.handler(params);
          
          // Handle feature result
          if (result) {
            // Create an AI response message
            const aiMessage = {
              role: "assistant",
              timestamp: new Date().toISOString(),
            };
            
            // Format the message based on result type
            switch (result.type) {
              case 'text':
              case 'error':
              case 'request_info':
              case 'success':
                aiMessage.content = result.content;
                break;
                
              case 'scan_result':
                // Generate a detailed text summary
                const summary = result.content?.summary || {};
                let textSummary = `Scan result for wallet ${result.wallet.address}:\nTotal Value: ${summary.totalValueUsd?.toFixed(2) ?? 'N/A'} USD (${summary.totalValueBtc ?? 0} sats)\nTokens found: ${summary.tokenCount ?? 0}\n`;

                if (result.content?.pipe?.length > 0) {
                  textSummary += `\nPIPE (${result.content.pipe.length}):\n`;
                  result.content.pipe.forEach(t => {
                    textSummary += `  - ${t.ticker.toUpperCase()} (${t.quantity.toLocaleString()}): ${t.totalValueUsd?.toFixed(2) ?? 'N/A'} USD (${t.totalValue ?? 0} sats)\n`;
                  });
                }
                if (result.content?.brc20?.length > 0) {
                  textSummary += `\nBRC-20 (${result.content.brc20.length}):\n`;
                  result.content.brc20.forEach(t => {
                    textSummary += `  - ${t.ticker.toUpperCase()} (${t.quantity.toLocaleString()}): ${t.totalValueUsd?.toFixed(2) ?? 'N/A'} USD (${t.totalValue ?? 0} sats)\n`;
                  });
                }
                if (result.content?.runes?.length > 0) {
                  textSummary += `\nRUNES (${result.content.runes.length}):\n`;
                  result.content.runes.forEach(t => {
                    const symbolDisplay = t.symbol ? ` ${t.symbol}` : '';
                    textSummary += `  - ${t.ticker.toUpperCase()}${symbolDisplay} (${t.quantity.toLocaleString()}): ${t.totalValueUsd?.toFixed(2) ?? 'N/A'} USD (${t.totalValue ?? 0} sats)\n`;
                  });
                }
                if (result.content?.tap?.length > 0) {
                  textSummary += `\nTAP (${result.content.tap.length}):\n`;
                  result.content.tap.forEach(t => {
                    textSummary += `  - ${t.ticker.toUpperCase()} (${t.quantity.toLocaleString()}): ${t.totalValueUsd?.toFixed(2) ?? 'N/A'} USD (${t.totalValue ?? 0} sats)\n`;
                  });
                }
                
                aiMessage.content = textSummary.trim(); // Text summary for context
                aiMessage.customHTML = createWalletScanResultHTML(result); // HTML for display
                break;
                
              case 'multiple_scan_results':
                // Generate a detailed summary for multiple results
                const overallSummaryMultiple = result.summary || {};
                const walletCountMultiple = result.content?.length || 0;
                let multiTextSummary = `Scan result for ${walletCountMultiple} wallets:\nOverall Total Value: ${overallSummaryMultiple.totalValueUsd?.toFixed(2) ?? 'N/A'} USD (${overallSummaryMultiple.totalValueBtc ?? 0} sats)\nTotal Tokens found: ${overallSummaryMultiple.tokenCount ?? 0}\n`;

                result.content.forEach((item, index) => {
                  const { wallet, result: walletResult, error } = item;
                  const walletSummary = walletResult?.summary || {};
                  multiTextSummary += `\n--- Wallet ${index + 1} (${wallet.address} - ${wallet.network}) ---\n`;
                  multiTextSummary += `Value: ${walletSummary.totalValueUsd?.toFixed(2) ?? 'N/A'} USD (${walletSummary.totalValueBtc ?? 0} sats), Tokens: ${walletSummary.tokenCount ?? 0}\n`;

                  if (error) {
                    multiTextSummary += `  Error: ${error}\n`;
                    return; // Skip to next wallet if error
                  }

                  if (walletResult?.pipe?.length > 0) {
                    multiTextSummary += `  PIPE (${walletResult.pipe.length}):\n`;
                    walletResult.pipe.forEach(t => {
                      multiTextSummary += `    - ${t.ticker.toUpperCase()} (${t.quantity.toLocaleString()}): ${t.totalValueUsd?.toFixed(2) ?? 'N/A'} USD (${t.totalValue ?? 0} sats)\n`;
                    });
                  }
                  if (walletResult?.brc20?.length > 0) {
                    multiTextSummary += `  BRC-20 (${walletResult.brc20.length}):\n`;
                    walletResult.brc20.forEach(t => {
                      multiTextSummary += `    - ${t.ticker.toUpperCase()} (${t.quantity.toLocaleString()}): ${t.totalValueUsd?.toFixed(2) ?? 'N/A'} USD (${t.totalValue ?? 0} sats)\n`;
                    });
                  }
                  if (walletResult?.runes?.length > 0) {
                    multiTextSummary += `  RUNES (${walletResult.runes.length}):\n`;
                    walletResult.runes.forEach(t => {
                      const symbolDisplay = t.symbol ? ` ${t.symbol}` : '';
                      multiTextSummary += `    - ${t.ticker.toUpperCase()}${symbolDisplay} (${t.quantity.toLocaleString()}): ${t.totalValueUsd?.toFixed(2) ?? 'N/A'} USD (${t.totalValue ?? 0} sats)\n`;
                    });
                  }
                  if (walletResult?.tap?.length > 0) {
                    multiTextSummary += `  TAP (${walletResult.tap.length}):\n`;
                    walletResult.tap.forEach(t => {
                      multiTextSummary += `    - ${t.ticker.toUpperCase()} (${t.quantity.toLocaleString()}): ${t.totalValueUsd?.toFixed(2) ?? 'N/A'} USD (${t.totalValue ?? 0} sats)\n`;
                    });
                  }
                });
                
                aiMessage.content = multiTextSummary.trim(); // Text summary for context
                aiMessage.customHTML = createMultipleWalletScanResultHTML(result); // HTML for display
                break;
                
              default:
                aiMessage.content = 'Received an unknown response from the feature.';
            }
            
            // Add AI message to conversation
            // Get fresh reference to conversations to avoid stale data
            const updatedConversations = store.getConversations();
            const updatedIndex = updatedConversations.findIndex(c => c.id === currentConversationId);
            
            if (updatedIndex !== -1) {
              updatedConversations[updatedIndex].messages.push(aiMessage);
              store.setConversations(updatedConversations);
              saveConversations();
              
              // Re-render messages with the new AI response
              renderMessages(updatedConversations[updatedIndex].messages);
              
              // Update title if this is a new conversation
              if (updatedConversations[updatedIndex].messages.length <= 2) {
                // First user message becomes the title (truncated)
                const newTitle = content.length > 30 ? content.substring(0, 30) + "..." : content;
                updatedConversations[updatedIndex].title = newTitle;
                store.setConversations(updatedConversations);
                saveConversations();
                renderConversationsList();
              }
            }
          }
        } catch (error) {
          console.error('Feature execution error:', error);
          
          // Add error message to conversation
          const errorMessage = {
            role: "assistant",
            content: `Error executing feature: ${error.message}`,
            timestamp: new Date().toISOString(),
          };
          
          const latestConversations = store.getConversations();
          const latestIndex = latestConversations.findIndex(c => c.id === currentConversationId);
          
          if (latestIndex !== -1) {
            latestConversations[latestIndex].messages.push(errorMessage);
            store.setConversations(latestConversations);
            saveConversations();
            
            // Re-render messages with error
            renderMessages(latestConversations[latestIndex].messages);
          }
        } finally {
          // Remove thinking indicator
          removeThinkingIndicator(currentConversationId);

          // Reset pending state for this conversation
          store.setPendingResponse(currentConversationId, false);
          
          // Re-enable input
          if (sendBtn) sendBtn.disabled = false;
          if (messageInput) messageInput.disabled = false;
        }
        
        return; // Exit after handling feature
      }
  
      // PROCEED WITH NORMAL MESSAGE HANDLING if not a feature
      // Disable send button to prevent double submissions
      if (sendBtn) sendBtn.disabled = true;
      if (messageInput) messageInput.disabled = true;
  
      // Check for guest mode limits
      if (store.getIsGuestMode()) {
        guestMessageCount = parseInt(
          localStorage.getItem("quantumGuestCount") || "0"
        );
        if (guestMessageCount >= guestMaxMessages) {
          showLimitReached();
          if (sendBtn) sendBtn.disabled = false;
          if (messageInput) messageInput.disabled = false;
          return;
        }
  
        guestMessageCount++;
        localStorage.setItem("quantumGuestCount", guestMessageCount.toString());
        saveGuestUsage();
        updateGuestUsageUI();
      }
  
      // Clear input and resize
      messageInput.value = "";
      messageInput.style.height = "auto";
  
      // Get current conversation
      const conversations = store.getConversations();
      const conversationIndex = conversations.findIndex(
        (c) => c.id === currentConversationId
      );
  
      // Récupérer et vérifier à nouveau après création
      if (conversationIndex === -1) {
        // Créer une nouvelle conversation et réessayer
        currentConversationId = createNewConversation();
        store.setCurrentConversationId(currentConversationId);
        
        // Réessayer avec la nouvelle conversation
        const updatedConversations = store.getConversations();
        const newIndex = updatedConversations.findIndex(c => c.id === currentConversationId);
        
        if (newIndex === -1) {
          showNotification("Erreur lors de la création de la conversation", "error");
          if (sendBtn) sendBtn.disabled = false;
          if (messageInput) messageInput.disabled = false;
          return;
        }
        
        // Continuer avec la nouvelle conversation
        const userMessage = {
          role: "user",
          content: content,
          timestamp: new Date().toISOString(),
        };
  
        updatedConversations[newIndex].messages.push(userMessage);
        store.setConversations(updatedConversations);
        saveConversations();
        
        // Re-render messages
        renderMessages(updatedConversations[newIndex].messages);
        
        // Update title
        updatedConversations[newIndex].title = content.length > 30 
          ? content.substring(0, 30) + "..." 
          : content;
        store.setConversations(updatedConversations);
        saveConversations();
        renderConversationsList();
        
        // Mark this conversation as having a pending response
        store.setPendingResponse(currentConversationId, true);
        addThinkingIndicator(currentConversationId);
        
        // Désactiver l'input pendant que l'IA réfléchit
        if (messageInput) messageInput.disabled = true;
        if (sendBtn) sendBtn.disabled = true;
        messageInput.placeholder = "Alaric is thinking...";
        
        // Send to API - THIS PART WAS MISSING
        const apiResponse = await sendMessageToAPI(content, currentConversationId);
        
        // Process response
        if (apiResponse) {
          await processApiResponse(apiResponse, newIndex, currentConversationId);
        }
      } else {
        // Normal flow with existing conversation
        const userMessage = {
          role: "user",
          content: content,
          timestamp: new Date().toISOString(),
        };
  
        conversations[conversationIndex].messages.push(userMessage);
        store.setConversations(conversations);
        saveConversations(); // Assuming saveConversations is synchronous or we don't need to await it here
  
        // Re-render messages
        renderMessages(conversations[conversationIndex].messages);
  
        // Mark this conversation as having a pending response
        store.setPendingResponse(currentConversationId, true);
        addThinkingIndicator(currentConversationId);
  
        // Désactiver l'input pendant que l'IA réfléchit
        const messageInput = document.getElementById("messageInput");
        const sendBtn = document.getElementById("sendBtn");
        if (messageInput) messageInput.disabled = true;
        if (sendBtn) sendBtn.disabled = true;
        messageInput.placeholder = "Alaric is thinking...";
        
        // Update conversation title if it's a new conversation
        if (conversations[conversationIndex].messages.length <= 2) {
          // First user message becomes the title (truncated)
          const newTitle =
            content.length > 30 ? content.substring(0, 30) + "..." : content;
          conversations[conversationIndex].title = newTitle;
          store.setConversations(conversations);
          saveConversations();
          renderConversationsList();
        }
        
        // Send to API - THIS PART WAS MISSING
        const apiResponse = await sendMessageToAPI(content, currentConversationId);
        
        // Process response
        if (apiResponse) {
          await processApiResponse(apiResponse, conversationIndex, currentConversationId);
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      showNotification("Une erreur est survenue", "error");
      const sendBtn = document.getElementById("sendBtn");
      const messageInput = document.getElementById("messageInput");
      if (sendBtn) sendBtn.disabled = false;
      if (messageInput) messageInput.disabled = false;
    } finally {
      // Ensure input is always enabled, even if something unexpected happens
      const messageInput = document.getElementById("messageInput");
      const sendBtn = document.getElementById("sendBtn");
      
      if (messageInput) {
        messageInput.disabled = false;
      }
      
      if (sendBtn) {
        sendBtn.disabled = messageInput && messageInput.value.trim() === "";
      }
    }
}

/**
 * Process the API response
 * @param {object} response - The API response
 * @param {number} conversationIndex - Index of the conversation in the array
 * @param {string} conversationId - ID of the conversation
 */
async function processApiResponse(response, conversationIndex, conversationId) {
  try {
    removeThinkingIndicator(conversationId);

    const conversations = store.getConversations();
    const sendBtn = document.getElementById("sendBtn");
    const messageInput = document.getElementById("messageInput");
    
    // Restaurer le placeholder par défaut
    if (messageInput) {
      messageInput.placeholder = "Envoyer un message...";
    }

    if (response.error) {
      const targetConversation = conversations.find(
        (c) => c.id === conversationId
      );

      if (targetConversation) {
        let errorMessage;

        if (
          response.error.includes("Insufficient balance") ||
          response.error.includes("Insufficient tokens")
        ) {
          const MIN_LANE_TOKENS = 1000;
          errorMessage = {
            role: "assistant",
            content: `Error: ${response.error} You need at least ${MIN_LANE_TOKENS} LANE tokens to access premium features.`,
          };
          showNotification(
            `Insufficient LANE tokens. You need at least ${MIN_LANE_TOKENS} LANE tokens for premium access.`,
            "error"
          );
        } else if (response.error.includes("Daily limit reached")) {
          errorMessage = {
            role: "assistant",
            content: `You've reached your daily message limit. Hold LANE tokens to access premium features with higher limits. <a href="#" class="learn-more-link">More details</a>`,
            isHtml: true,
          };

          if (sendBtn) sendBtn.disabled = true;
          if (messageInput) {
            messageInput.disabled = true;
            messageInput.placeholder =
              "Daily limit reached. Please try again tomorrow or get premium access.";
          }

          showNotification(
            "Daily message limit reached. Hold LANE tokens for higher limits.",
            "error"
          );
        } else {
          errorMessage = {
            role: "assistant",
            content: `Error: ${response.error}`,
          };
        }

        targetConversation.messages.push(errorMessage);

        if (conversationId === store.getCurrentConversationId()) {
          renderMessages(targetConversation.messages);
        }

        store.setConversations(conversations);
        saveConversations();
      }
      return;
    }

    const targetConversation = conversations.find(
      (c) => c.id === conversationId
    );

    if (targetConversation) {
      let responseText = "";

      if (
        response.messages &&
        Array.isArray(response.messages) &&
        response.messages.length > 0
      ) {
        responseText = response.messages[0].text;
      } else if (typeof response === "object" && response !== null) {
        if (response.text) {
          responseText = response.text;
        } else {
          responseText = JSON.stringify(response, null, 2);
        }
      } else {
        responseText = response;
      }

      const aiMessage = {
        role: "assistant",
        content: responseText,
      };

      targetConversation.messages.push(aiMessage);

      if (conversationId === store.getCurrentConversationId()) {
        renderMessages(targetConversation.messages);
      }

      renderConversationsList();
      store.setConversations(conversations);
      saveConversations();
    }
  } catch (error) {
    console.error("Error getting response:", error);
    removeThinkingIndicator(conversationId);

    const conversations = store.getConversations();
    const targetConversation = conversations.find(
      (c) => c.id === conversationId
    );

    if (targetConversation) {
      const errorMessage = {
        role: "assistant",
        content: "I'm sorry, I encountered an error. Please try again later.",
      };

      targetConversation.messages.push(errorMessage);

      if (conversationId === store.getCurrentConversationId()) {
        renderMessages(targetConversation.messages);
      }

      store.setConversations(conversations);
      saveConversations();
    }
  } finally {
    store.setPendingResponse(conversationId, false);

    const messageInput = document.getElementById("messageInput");
    const sendBtn = document.getElementById("sendBtn");

    if (conversationId === store.getCurrentConversationId() && sendBtn) {
      sendBtn.disabled = messageInput && messageInput.value.trim() === "";
    }
    
    // Always make sure the input is enabled regardless of the current conversation
    if (messageInput) {
      messageInput.disabled = false;
      // Ensure focus is back on the input for a better user experience
      messageInput.focus();
    }
  }
}

/**
 * Send a message to the API
 * @param {string} message - The message to send
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<object>} - The API response
 */
async function sendMessageToAPI(message, conversationId) {
  try {
    const authToken = localStorage.getItem("quantumAuthToken");
    if (!authToken) {
      showNotification("Session expired. Please reconnect.", "error");

      // Import needed to avoid circular dependency
      const { performLogout } = await import("./authentication.js");
      performLogout();
      return null;
    }

    const conversations = store.getConversations();
    const conversation = conversations.find((c) => c.id === conversationId);
    if (!conversation) return null;

    const previousMessages = conversation.messages.slice(
      -contextMessageCount - 1,
      conversation.messages.length - 1
    );

    const response = await fetch(`${apiUrl}/${agentId}/message`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        text: message,
        roomId: conversationId,
        walletAddress: store.getWalletAddress(),
        previousMessages,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      const errorText = await response.text();
      try {
        return JSON.parse(errorText);
      } catch (e) {
        return { error: errorText || "Unknown error" };
      }
    }
  } catch (error) {
    throw new Error(error.message);
  }
}

/**
 * Load guest usage data from storage
 */
function loadGuestUsage() {
  const today = new Date().toDateString();
  const usage = localStorage.getItem("quantumGuestUsage");

  if (usage) {
    const usageData = JSON.parse(usage);
    if (usageData.date === today) {
      guestMessageCount = usageData.count;
      updateGuestUsageUI();
    } else {
      guestMessageCount = 0;
      saveGuestUsage();
    }
  } else {
    saveGuestUsage();
  }
}

/**
 * Save guest usage data to storage
 */
function saveGuestUsage() {
  const today = new Date().toDateString();
  localStorage.setItem(
    "quantumGuestUsage",
    JSON.stringify({
      date: today,
      count: guestMessageCount,
    })
  );

  updateGuestUsageUI();
}

/**
 * Update the guest usage UI
 */
function updateGuestUsageUI() {
  const usageInfo = document.getElementById("usageInfo");
  if (!usageInfo) return;

  if (store.getIsGuestMode()) {
    usageInfo.style.display = "block";
    const usageLabel = usageInfo.querySelector(".usage-label");
    const usageBar = usageInfo.querySelector(".usage-bar");

    if (usageLabel) {
      usageLabel.textContent = `Guest mode: ${guestMessageCount}/${guestMaxMessages} messages used today`;
    }

    if (usageBar) {
      usageBar.style.width = `${(guestMessageCount / guestMaxMessages) * 100}%`;
    }
  } else {
    usageInfo.style.display = "none";
  }
}

/**
 * Shows the limit reached modal when guest messages are exhausted
 */
function showLimitReached() {
  const limitReachedModal = document.getElementById("limitReachedModal");
  if (limitReachedModal) {
    limitReachedModal.style.display = "flex";
  }
}

// Add this to messages.js or create a new module called suggestions.js

// Initial set of suggestions
const initialSuggestions = [
  "Trending coins?",
  "Any news?",
  "Add my wallet",
  "Scan my wallet",
  "Pros and Cons of $FARTCOIN",
  "Scan the website: lumia.org",
];

/**
 * Initialize the suggestions component
 */
function setupSuggestions() {
  // Vérifier si le conteneur existe déjà
  if (document.getElementById("suggestionsContainer")) {
    // Le conteneur existe déjà, pas besoin d'en créer un nouveau
    return;
  }

  const inputContainer = document.querySelector(".input-container");
  const inputWrapper = document.querySelector(".input-wrapper");

  if (!inputContainer || !inputWrapper) return;

  // Create suggestions container
  const suggestionsContainer = document.createElement("div");
  suggestionsContainer.className = "suggestions-container";
  suggestionsContainer.id = "suggestionsContainer";

  // Add the suggestions before the input wrapper
  inputContainer.insertBefore(suggestionsContainer, inputWrapper);

  // Render the initial suggestions
  renderSuggestions(initialSuggestions);
  
  // Ajouter un écouteur d'événement pour le redimensionnement de la fenêtre
  window.addEventListener('resize', () => {
    // Récupérer les suggestions actuelles (si possible)
    const currentSuggestions = getCurrentSuggestions();
    if (currentSuggestions.length > 0) {
      renderSuggestions(currentSuggestions);
    } else {
      renderSuggestions(initialSuggestions);
    }
  });
}

/**
 * Récupère les suggestions actuellement affichées
 * @returns {Array} - Tableau des suggestions actuelles
 */
function getCurrentSuggestions() {
  const container = document.getElementById("suggestionsContainer");
  if (!container) return [];
  
  const chips = container.querySelectorAll('.suggestion-chip');
  return Array.from(chips).map(chip => chip.textContent);
}

/**
 * Render suggestion chips in the container
 * @param {Array} suggestions - Array of suggestion strings
 */
function renderSuggestions(suggestions) {
  const container = document.getElementById("suggestionsContainer");
  if (!container) return;

  container.innerHTML = "";
  
  // Limiter le nombre de suggestions sur les petits écrans
  const isMobile = window.innerWidth <= 768;
  // Utiliser plus de suggestions sur mobile mais plus courtes
  const displaySuggestions = isMobile ? 
    // Sur mobile, on prend les 5 premières suggestions
    suggestions.slice(0, 6) : 
    suggestions;

  displaySuggestions.forEach((suggestion) => {
    const chip = document.createElement("div");
    chip.className = "suggestion-chip";
    
    // Si on est sur mobile et que la suggestion est longue, on la tronque
    if (isMobile && suggestion.length > 20) {
      chip.textContent = suggestion.substring(0, 18) + '...';
      chip.title = suggestion; // Afficher le texte complet au survol
    } else {
      chip.textContent = suggestion;
    }

    chip.addEventListener("click", () => {
      // Toujours utiliser la suggestion complète, même si elle est tronquée à l'affichage
      useSuggestion(chip.title || suggestion);
    });

    container.appendChild(chip);
  });
}

/**
 * Use a suggestion by filling the input and triggering send
 * @param {string} suggestion - The suggestion text
 */
function useSuggestion(suggestion) {
  // Cas spéciaux pour les fonctionnalités de wallet
  if (suggestion === "Scan my wallet") {
    // Appeler directement la fonctionnalité scan_my_wallet du featureSystem
    if (typeof featureSystem !== 'undefined') {
      featureSystem.activateFeatureFromButton('scan_my_wallet');
      return;
    }
  } else if (suggestion === "Add my wallet") {
    // Appeler directement la fonctionnalité add_my_wallet du featureSystem
    if (typeof featureSystem !== 'undefined') {
      featureSystem.activateFeatureFromButton('add_my_wallet');
      return;
    }
  }

  // Comportement standard pour les autres suggestions
  const messageInput = document.getElementById("messageInput");
  const sendBtn = document.getElementById("sendBtn");

  if (messageInput && sendBtn) {
    messageInput.value = suggestion;
    messageInput.focus();

    // Trigger input event to enable send button
    const inputEvent = new Event("input", {
      bubbles: true,
      cancelable: true,
    });
    messageInput.dispatchEvent(inputEvent);

    // Optional: automatically send after a short delay
    setTimeout(() => {
      sendBtn.click();
    }, 100);
  }
}

/**
 * Update suggestions based on conversation context
 * @param {Array} messages - Recent messages in the conversation
 */
function updateContextualSuggestions(messages) {
  // This is where you would implement logic to generate
  // contextual suggestions based on the conversation
  // For now, we'll use a simple approach

  if (!messages || messages.length === 0) {
    return renderSuggestions(initialSuggestions);
  }

  // Look at the most recent AI message
  const lastAiMessage = [...messages]
    .reverse()
    .find((m) => m.role === "assistant");

  if (!lastAiMessage) {
    return renderSuggestions(initialSuggestions);
  }

  // Simple keyword-based suggestion sets
  const btcKeywords = ["bitcoin", "btc", "satoshi", "crypto"];
  const ethKeywords = ["ethereum", "eth", "vitalik", "smart contract"];
  const marketKeywords = ["market", "price", "trend", "analysis", "chart"];
  const defiKeywords = ["defi", "yield", "farming", "liquidity", "staking"];
  const nftKeywords = ["nft", "collectible", "art", "token", "opensea"];

  const content = lastAiMessage.content.toLowerCase();

  if (btcKeywords.some((kw) => content.includes(kw))) {
    renderSuggestions([
      "BTC price prediction?",
      "Bitcoin ETF impact",
      "Mining profitability",
      "Bitcoin adoption trends",
      "BTC vs. gold comparison",
    ]);
  } else if (ethKeywords.some((kw) => content.includes(kw))) {
    renderSuggestions([
      "ETH 2.0 status?",
      "ETH price outlook",
      "Layer 2 solutions",
      "Best ETH alternatives",
      "Smart contract risks",
    ]);
  } else if (marketKeywords.some((kw) => content.includes(kw))) {
    renderSuggestions([
      "Market sentiment?",
      "Technical analysis",
      "Best buying strategy",
      "Risk management tips",
      "Top performing assets",
    ]);
  } else if (defiKeywords.some((kw) => content.includes(kw))) {
    renderSuggestions([
      "DeFi yield comparison",
      "Staking rewards",
      "Liquidity pools risks",
      "Best DeFi protocols",
      "DeFi security tips",
    ]);
  } else if (nftKeywords.some((kw) => content.includes(kw))) {
    renderSuggestions([
      "NFT market overview",
      "NFT evaluating criteria",
      "Top NFT projects",
      "NFT marketplaces",
      "NFT future trends",
    ]);
  } else {
    // Default back to initial suggestions
    renderSuggestions(initialSuggestions);
  }
}

export {
  setupMessageHandling,
  setupMessageMenus,
  handleInputChange,
  sendMessage,
  processApiResponse,
  reportInaccurateContent,
  showLimitReached,
  setupSuggestions,
  updateContextualSuggestions,
  useSuggestion,
  getCurrentSuggestions,
};
